import { useMutation, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { toast } from "sonner";
import { courseEndpoints } from "@/config/endpoints";

export function useCourseMutations() {
  const queryClient = useQueryClient();

  // 1. CREATE COURSE
  const createCourse = useMutation({
    mutationFn: async (data: any) => {
      return axiosInstance.post(courseEndpoints, data);
    },
    onSuccess: (res: any) => {
      // `courses` lists every row when the same course went into several
      // universities at once (multi-picker); a single create has one.
      const n = Array.isArray(res?.data?.courses) ? res.data.courses.length : 1;
      toast.success(
        n > 1
          ? `Course created in ${n} universities`
          : "Course created successfully",
      );
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      // University cards carry a course count.
      queryClient.invalidateQueries({ queryKey: ["universities"] });
    },
    onError: (error: any) => {
      console.error(error);
      const msg = error.response?.data?.message || "Failed to create course";
      toast.error(msg);
    },
  });

  // 2. UPDATE COURSE
  const updateCourse = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      return axiosInstance.patch(`${courseEndpoints}/${id}`, data);
    },
    onSuccess: (_res, { id }) => {
      toast.success("Course updated successfully");
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      // The Edit drawer reads `["get-course", id]` (useCurrentCourse), which
      // is cached for 3 min — only the LIST was refreshed, so reopening the
      // course right after saving showed the OLD intake months / fields
      // ("Intake Month not updating", 2026-09-08).
      queryClient.invalidateQueries({ queryKey: ["get-course", id] });
    },
    onError: (error: any) => {
      const msg = error.response?.data?.message || "Failed to update course";
      toast.error(msg);
    },
  });

  // 3. DELETE COURSE
  const deleteCourse = useMutation({
    mutationFn: async (id: number) => {
      return axiosInstance.delete(`${courseEndpoints}/${id}`);
    },
    onSuccess: (_res, id) => {
      toast.success("Course deleted");
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      queryClient.removeQueries({ queryKey: ["get-course", id] });
    },
    onError: () => {
      toast.error("Failed to delete course");
    },
  });

  return { createCourse, updateCourse, deleteCourse };
}
